import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ListColumnType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useSettings } from 'services/settings/SettingsContext';
import { formatDate } from 'services/utils/dateTimeUtils';
import { renderNewsletterStatusBadge } from './NewsletterStatusBadges';
import { NewsletterListItem, NEWSLETTER_PATH, newsletterPagePath } from './NewsletterTypes';

// Lists the newsletters with the concepts on top, a concept opens the editor and a sent newsletter its report
const NewsletterList: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { userLocale } = useSettings();

    // Specify the options of this list
    const query = {
        endpoint: 'get_newsletter_list',
        defaultParams: { 'deleted': false },
        object: 'newsletter'
    };
    const objectName = 'newsletters.object_name';
    const showSearch = true;
    const showDeleteOrDeactivate = 'flag_deleted';
    const isPaginated = true;

    // Open the editor for a new newsletter
    const openPageUrl = `${NEWSLETTER_PATH.slice(1)}/new`;

    // Open the editor or the report of the clicked newsletter
    const onRowClick = (row: any) => history.push(newsletterPagePath(row as NewsletterListItem));

    // Specify the columns of this list, the order is fixed so the concepts stay on top
    const columns: ListColumnType[] = useMemo(() => [
        {
            width: '6fr', field: 'subject', label: 'newsletters.list.column_subject', disableOrdering: true,
            render: (value: string | null, row: any) => (
                <span className='newsletter-subject-cell'>
                    <span className='newsletter-subject-text'>{value || t('newsletters.general.untitled')}</span>
                    {row.status !== 'concept' && (
                        <span className='newsletter-recipient-suffix'>({t('newsletters.list.recipients_suffix', { count: row.recipient_count })})</span>
                    )}
                    {row.is_release && (
                        <span className='status-badge small newsletter-release'>
                            {row.release_number ? t('newsletters.list.release_badge', { number: row.release_number }) : t('newsletters.list.release_badge_concept')}
                        </span>
                    )}
                </span>
            ),
        },
        {
            width: '1.5fr', field: 'sent_at', label: 'newsletters.list.column_date', disableOrdering: true, customClass: 'newsletter-column-right',
            render: (value: string | null, row: any) => <span>{formatDate(value ?? row.modified, userLocale)}</span>,
        },
        {
            width: '1.25fr', field: 'status', label: 'newsletters.list.column_status', disableOrdering: true, customClass: 'newsletter-column-right',
            render: (value: any) => renderNewsletterStatusBadge(value),
        },
    ], [t, userLocale]);

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            tableClassName='newsletter-list-table'
            showSearch={showSearch}
            showDeleteOrDeactivate={showDeleteOrDeactivate}
            openPageUrl={openPageUrl}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            buttonSize='small'
        />
    );
};

export default NewsletterList;
